import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Globe2,
  History,
  KeyRound,
  Loader2,
  PlayCircle,
  RefreshCw,
  Server,
  ShieldCheck,
  X,
} from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("id-ID");
};

const rowsFrom = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.items || payload?.rows || payload?.history || [];
};

const badgeClass = (status) => {
  const value = String(status || "").toUpperCase();
  if (["ACTIVE", "SUCCESS"].includes(value)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (["RUNNING", "STARTED", "ACTIVATING"].includes(value)) {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  if (["FAILED", "ERROR", "EXPIRED"].includes(value)) {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-600";
};

function StatusBadge({ children }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${badgeClass(children)}`}>
      {children || "-"}
    </span>
  );
}

export default function PlatformSSL() {
  const [certificate, setCertificate] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [dryStatus, setDryStatus] = useState(null);
  const [productionStatus, setProductionStatus] = useState(null);
  const [security, setSecurity] = useState(null);

  const [loading, setLoading] = useState(true);
  const [dryRunning, setDryRunning] = useState(false);
  const [productionRunning, setProductionRunning] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [productionForm, setProductionForm] = useState({
    current_password: "",
    totp_code: "",
    confirmation: "",
  });

  const dryTimer = useRef(null);
  const productionTimer = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");

    const calls = await Promise.allSettled([
      api.get("/platform/ssl/status"),
      api.get("/platform/ssl/history"),
      api.get("/platform/ssl/renewal/status"),
      api.get("/platform/ssl/renewal/production/status"),
      api.get("/platform/ssl/renewal/security"),
      api.get("/platform/ssl/renewal/audit"),
    ]);

    if (calls[0].status === "fulfilled") setCertificate(calls[0].value.data);
    if (calls[1].status === "fulfilled") setHistoryRows(rowsFrom(calls[1].value.data));
    if (calls[2].status === "fulfilled") setDryStatus(calls[2].value.data);
    if (calls[3].status === "fulfilled") setProductionStatus(calls[3].value.data);
    if (calls[4].status === "fulfilled") setSecurity(calls[4].value.data);
    if (calls[5].status === "fulfilled") setAuditRows(rowsFrom(calls[5].value.data));

    if (calls[0].status === "rejected") {
      setError(apiErrorMessage(calls[0].reason, "Gagal membaca certificate SSL."));
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
    return () => {
      if (dryTimer.current) clearTimeout(dryTimer.current);
      if (productionTimer.current) clearTimeout(productionTimer.current);
    };
  }, [loadAll]);

  const pollDry = useCallback(async () => {
    try {
      const response = await api.get("/platform/ssl/renewal/status");
      setDryStatus(response.data);
      const state = String(response.data?.state || "").toUpperCase();
      if (["SUCCESS", "FAILED", "ERROR"].includes(state)) {
        setDryRunning(false);
        setMessage(
          state === "SUCCESS"
            ? "Dry run SSL selesai dan validasi renewal berhasil."
            : ""
        );
        if (state !== "SUCCESS") {
          setError(response.data?.error || "Dry run SSL gagal.");
        }
        await loadAll();
        return;
      }
    } catch (e) {
      setDryRunning(false);
      setError(apiErrorMessage(e, "Gagal membaca status dry run."));
      return;
    }
    dryTimer.current = setTimeout(pollDry, 2000);
  }, [loadAll]);

  const runDry = async () => {
    setError("");
    setMessage("");
    setDryRunning(true);
    try {
      const response = await api.post("/platform/ssl/renewal/test");
      if (!response.data?.success) throw new Error(response.data?.error || "Dry run gagal");
      setMessage("Dry run dimulai di background.");
      dryTimer.current = setTimeout(pollDry, 1500);
    } catch (e) {
      setDryRunning(false);
      setError(apiErrorMessage(e, e.message || "Dry run gagal."));
    }
  };

  const pollProduction = useCallback(async () => {
    try {
      const response = await api.get("/platform/ssl/renewal/production/status");
      setProductionStatus(response.data);
      const state = String(response.data?.state || "").toUpperCase();

      if (["SUCCESS", "FAILED", "ERROR"].includes(state)) {
        setProductionRunning(false);
        if (state === "SUCCESS") {
          setMessage("Production SSL renewal selesai. Certificate dan Nginx sudah diverifikasi.");
        } else {
          setError(response.data?.error || "Production SSL renewal gagal. Periksa journal service.");
        }
        await loadAll();
        return;
      }
    } catch (e) {
      setProductionRunning(false);
      setError(apiErrorMessage(e, "Gagal membaca status production renewal."));
      return;
    }
    productionTimer.current = setTimeout(pollProduction, 2000);
  }, [loadAll]);

  const submitProduction = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setProductionRunning(true);

    try {
      const response = await api.post(
        "/platform/ssl/renewal/run",
        productionForm
      );

      if (!response.data?.success) {
        throw new Error(response.data?.error || "Production renewal gagal dimulai.");
      }

      setModalOpen(false);
      setProductionForm({
        current_password: "",
        totp_code: "",
        confirmation: "",
      });
      setMessage("Production renewal diterima dan sedang diproses.");
      productionTimer.current = setTimeout(pollProduction, 1500);
    } catch (e) {
      setProductionRunning(false);
      setError(apiErrorMessage(e, e.message || "Production renewal gagal."));
    }
  };

  const productionBlocked =
    !security?.totp_enabled ||
    productionRunning ||
    String(productionStatus?.state || "").toUpperCase() === "RUNNING";

  return (
    <div className="p-5 sm:p-7 lg:p-10" data-testid="platform-ssl-management-page">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">
            PLATFORM OWNER
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-[-0.04em] text-slate-950">
            SSL Management
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            Monitoring certificate, dry run, production renewal, dan audit SSL KRAYA.
          </p>
        </div>

        <button
          type="button"
          onClick={loadAll}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm hover:border-blue-300 hover:text-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mt-6 flex gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {message && (
        <div className="mt-6 flex gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
          <div>{message}</div>
        </div>
      )}

      <section className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Certificate</span>
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div className="mt-5"><StatusBadge>{certificate?.status || "UNKNOWN"}</StatusBadge></div>
          <div className="mt-3 text-sm text-slate-500">{certificate?.issuer || "-"}</div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Domain</span>
            <Globe2 className="h-5 w-5 text-blue-600" />
          </div>
          <div className="mt-5 text-xl font-black text-slate-950">{certificate?.domain || "kraya.co.id"}</div>
          <div className="mt-3 text-xs text-slate-500">Local Let&apos;s Encrypt certificate</div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Berlaku hingga</span>
            <Clock3 className="h-5 w-5 text-blue-600" />
          </div>
          <div className="mt-5 text-lg font-black text-slate-950">{formatDate(certificate?.expired_at)}</div>
          <div className="mt-3 text-sm text-slate-500">Sisa <strong>{certificate?.remaining_days ?? "-"}</strong> hari</div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Production Engine</span>
            <Server className="h-5 w-5 text-blue-600" />
          </div>
          <div className="mt-5"><StatusBadge>{productionStatus?.state || "READY"}</StatusBadge></div>
          <div className="mt-3 text-sm text-slate-500">Certbot + Nginx reload</div>
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-6 lg:p-7">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">RENEWAL CONTROL</div>
          <h2 className="mt-2 text-2xl font-black text-slate-950">SSL Renewal</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            Dry run untuk validasi. Production renewal hanya dapat dijalankan Platform Owner dengan step-up authentication.
          </p>
        </div>

        <div className="grid gap-6 p-6 lg:grid-cols-2 lg:p-7">
          <div className="rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-black text-slate-950">Dry Run</div>
                <div className="mt-1 text-xs text-slate-500">Tidak mengubah certificate production.</div>
              </div>
              <StatusBadge>{dryStatus?.state || "READY"}</StatusBadge>
            </div>

            <button
              type="button"
              onClick={runDry}
              disabled={dryRunning}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3.5 text-sm font-bold text-white hover:bg-blue-600 disabled:opacity-50"
            >
              {dryRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              {dryRunning ? "Menjalankan..." : "Jalankan Dry Run"}
            </button>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-black text-slate-950">Production Renewal</div>
                <div className="mt-1 text-xs text-slate-500">Actual Certbot production + validasi + reload Nginx.</div>
              </div>
              <StatusBadge>{productionStatus?.state || "READY"}</StatusBadge>
            </div>

            {!security?.totp_enabled && (
              <div className="mt-4 flex gap-2 rounded-xl border border-amber-200 bg-white p-3 text-xs leading-5 text-amber-900">
                <KeyRound className="mt-0.5 h-4 w-4 shrink-0" />
                Aktifkan Google Authenticator pada Keamanan Akun sebelum production renewal.
              </div>
            )}

            <button
              type="button"
              onClick={() => setModalOpen(true)}
              disabled={productionBlocked}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3.5 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              {productionRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {productionRunning ? "Renewal berjalan..." : "Renew Production"}
            </button>
          </div>
        </div>
      </section>

      <section className="mt-8 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 p-6 lg:p-7">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">AUDIT</div>
            <h2 className="mt-2 text-2xl font-black text-slate-950">Renewal Audit Log</h2>
          </div>
          <History className="h-5 w-5 text-slate-400" />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-6 py-4">Time</th>
                <th className="px-6 py-4">Action</th>
                <th className="px-6 py-4">User</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Certificate Changed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {auditRows.map((row) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap px-6 py-4 text-slate-600">{formatDate(row.created_at)}</td>
                  <td className="whitespace-nowrap px-6 py-4 font-semibold text-slate-900">{row.action}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-slate-600">{row.user_email || row.user_name || row.user_id || "-"}</td>
                  <td className="whitespace-nowrap px-6 py-4"><StatusBadge>{row.status}</StatusBadge></td>
                  <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                    {row.details?.certificate_changed === true
                      ? "Ya"
                      : row.details?.certificate_changed === false
                      ? "Tidak"
                      : "-"}
                  </td>
                </tr>
              ))}
              {!auditRows.length && (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-500">Belum ada audit renewal.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-8 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-6 lg:p-7">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">MONITORING</div>
          <h2 className="mt-2 text-2xl font-black text-slate-950">Certificate History</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-6 py-4">Checked</th>
                <th className="px-6 py-4">Domain</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Issuer</th>
                <th className="px-6 py-4">Expired</th>
                <th className="px-6 py-4 text-right">Remaining</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {historyRows.map((row, index) => (
                <tr key={`${row.id || row.checked_at}-${index}`}>
                  <td className="whitespace-nowrap px-6 py-4 text-slate-600">{formatDate(row.checked_at)}</td>
                  <td className="whitespace-nowrap px-6 py-4 font-semibold">{row.domain || "-"}</td>
                  <td className="whitespace-nowrap px-6 py-4"><StatusBadge>{row.status}</StatusBadge></td>
                  <td className="whitespace-nowrap px-6 py-4 text-slate-600">{row.issuer || "-"}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-slate-600">{formatDate(row.expired_at)}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-right font-bold">{row.remaining_days ?? "-"} hari</td>
                </tr>
              ))}
              {!historyRows.length && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-slate-500">Belum ada certificate history.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 p-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-rose-600">CRITICAL ACTION</div>
                <h3 className="mt-2 text-2xl font-black text-slate-950">Renew SSL Production</h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  KRAYA akan menjalankan Certbot production, memvalidasi Nginx, lalu reload Nginx.
                </p>
              </div>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submitProduction} className="space-y-5 p-6">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Password Platform Owner</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={productionForm.current_password}
                  onChange={(e) => setProductionForm({ ...productionForm, current_password: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">Google Authenticator</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={productionForm.totp_code}
                  onChange={(e) =>
                    setProductionForm({
                      ...productionForm,
                      totp_code: e.target.value.replace(/\D/g, "").slice(0, 6),
                    })
                  }
                  placeholder="6 digit"
                  className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 font-mono tracking-[0.35em] outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Ketik: <span className="font-mono text-rose-600">{security?.production_confirmation || "RENEW KRAYA SSL"}</span>
                </span>
                <input
                  type="text"
                  autoComplete="off"
                  value={productionForm.confirmation}
                  onChange={(e) => setProductionForm({ ...productionForm, confirmation: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 font-mono outline-none focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                  required
                />
              </label>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-900">
                Certbot hanya memperbarui certificate jika certificate sudah memenuhi syarat renewal. Tombol ini tidak menggunakan <code>--force-renewal</code>.
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={
                    productionRunning ||
                    productionForm.totp_code.length !== 6 ||
                    productionForm.confirmation !== (security?.production_confirmation || "RENEW KRAYA SSL")
                  }
                  className="flex-1 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-bold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {productionRunning ? "Memproses..." : "Konfirmasi Renewal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
